import api from './api';
import accountStore from './accountStore';

/**
 * Persist a login/refresh response body onto local storage AND return the
 * user object (unchanged shape from before). Every server response that mints
 * or rotates a session flows through here.
 */
function absorbSession(res) {
  const accessToken = res?.accessToken || res?.token;
  if (accessToken) accountStore.setAccessToken(accessToken);
  if (res?.account && res?.refreshToken) {
    accountStore.upsertAccount(res.account, res.refreshToken);
  } else if (res?.data?._id && res?.refreshToken) {
    // Fallback if `account` isn't in the response for some reason — derive
    // the tile from the full user payload.
    const u = res.data;
    accountStore.upsertAccount(
      { id: String(u._id), name: u.name, email: u.email, role: u.role },
      res.refreshToken,
    );
  }
  return res.data;
}

const authService = {
  /** Standard email + password login. On success the account is saved locally. */
  async login(email, password) {
    const res = await api.post('/auth/login', { email, password });
    return absorbSession(res);
  },

  /**
   * Silent token renewal. Called by:
   *   • The 401 interceptor in api.js when the access token expires.
   *   • switchAccount() to activate a different saved account.
   *
   * Pass a specific refreshToken to switch accounts; omit to renew the
   * currently active session (the cookie carries the token in that case).
   */
  async refresh(refreshToken) {
    const body = refreshToken ? { refreshToken } : {};
    const res = await api.post('/auth/refresh', body, { _skipAuthRefresh: true });
    return absorbSession(res);
  },

  async me() {
    const res = await api.get('/auth/me');
    return res.data;
  },

  /**
   * Log out the CURRENT session (this browser's active account). Other saved
   * accounts stay signed in. If no account is active, still calls the server
   * so it can clear a stray cookie.
   */
  async logout() {
    const active = accountStore.getActiveAccount();
    try {
      await api.post('/auth/logout', active?.refreshToken ? { refreshToken: active.refreshToken } : {});
    } catch { /* server call best-effort — always clear local state */ }
    accountStore.setAccessToken(null);
    if (active) {
      const next = accountStore.removeAccount(active.id);
      // If there's another saved account, activate it silently.
      if (next?.refreshToken) {
        try { return await authService.refresh(next.refreshToken); }
        catch { /* fall through to logged-out state */ }
      }
    }
    return null;
  },

  /**
   * Revoke ONE saved account (both server-side and locally). Does NOT change
   * the currently active session unless the removed account was the active
   * one — in that case we fall back to the next saved account or logged-out.
   */
  async removeAccount(userId) {
    const target = accountStore.getAccount(userId);
    const active = accountStore.getActiveAccount();
    if (target?.refreshToken) {
      try {
        await api.post('/auth/logout', { refreshToken: target.refreshToken });
      } catch { /* best-effort — still remove from local store */ }
    }
    const next = accountStore.removeAccount(userId);
    // If we removed the active user, fail over to another saved account.
    if (active && active.id === String(userId)) {
      accountStore.setAccessToken(null);
      if (next?.refreshToken) {
        try { return await authService.refresh(next.refreshToken); }
        catch { return null; }
      }
      return null;
    }
    return { data: null }; // active user unchanged
  },

  /**
   * One-click account switch — silently trades the target account's refresh
   * token for a fresh access token. If the refresh fails (revoked / expired
   * on the server), we remove the stale local entry and let the UI prompt
   * for a fresh sign-in.
   */
  async switchAccount(userId) {
    const target = accountStore.getAccount(userId);
    if (!target?.refreshToken) {
      throw { code: 'NO_REFRESH_TOKEN', message: 'This account needs to sign in again.' };
    }
    try {
      return await authService.refresh(target.refreshToken);
    } catch (err) {
      // Stale saved account — clean up so the UI shows "sign in" for it next time.
      accountStore.removeAccount(userId);
      throw err;
    }
  },

  /** Revoke every session for the current user across all devices. */
  async logoutAll() {
    try { await api.post('/auth/logout-all'); }
    catch { /* best-effort */ }
    accountStore.clearAll();
    return null;
  },

  async changePassword(currentPassword, newPassword) {
    const res = await api.put('/auth/change-password', { currentPassword, newPassword });
    // Password change rotates sessions on the server — absorb the new pair.
    if (res?.accessToken || res?.refreshToken) absorbSession(res);
    return res;
  },
  async forgotPassword(email) {
    return api.post('/auth/forgot-password', { email });
  },
  async resetPassword(token, password) {
    return api.post('/auth/reset-password', { token, password });
  },
  async listSessions() {
    return api.get('/auth/sessions');
  },
  getToken() {
    return accountStore.getAccessToken();
  },
  // Handy read-only accessors so components don't reach into localStorage.
  listAccounts() { return accountStore.listAccounts(); },
  activeAccount() { return accountStore.getActiveAccount(); },
};

export default authService;
